import React, {
  CSSProperties,
  useMemo,
  useCallback,
  useState,
  useEffect,
} from 'react';
import { Cell, flexRender, Row, Table } from '@tanstack/react-table';
import classNames from 'classnames';
import { SkeletonLoading } from '@saleshandy/design-system';

import Checkbox from '../../../components/atoms/checkbox';
import { OverlayTooltip, Placement } from '../../../components/overlay';
import { accessibleOnClick } from '../../../../utils/accessible-on-click';

type IProps = {
  table: Table<any>;
  row: Row<any>;
  cell: Cell<any, any>;
  cellIndex: number;
  onRowClick?: ((row: any) => void) | null;
  isRowSelected: boolean;
  onRowSelectHandler: (rowId: number, isSelect: boolean) => void;
  isDisabled: boolean;
  getRowDisabledTooltip?: ((row: any) => string) | null;
  rowData?: any;
  isLoading?: boolean;
  enableRowSelection?: boolean;
  /** Snapshot from parent render; comparing row.getIsExpanded() in memo is wrong (same Row ref reads live state). */
  isRowExpanded?: boolean;
};

const ColumnCell: React.FC<IProps> = ({
  // table is passed for future extensibility and consistency with original implementation
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  table,
  row,
  cell,
  cellIndex,
  onRowClick,
  isRowSelected,
  onRowSelectHandler,
  isDisabled,
  getRowDisabledTooltip = null,
  rowData = null,
  isLoading = false,
  enableRowSelection = true,
}) => {
  const { column } = cell;
  const columnId = column.id;
  const isFirstColumn = cellIndex === 0;
  const rowId = row.original.id;

  // Optimistic checkbox state: flip the checkbox the instant the user clicks,
  const [optimisticChecked, setOptimisticChecked] = useState(isRowSelected);
  useEffect(() => {
    setOptimisticChecked(isRowSelected);
  }, [isRowSelected]);

  // Memoize expensive calculations
  const columnCellStyles = useMemo((): CSSProperties => {
    const isPinned = column.getIsPinned();
    const columnDefSize = (column.columnDef.size as number) || 0;
    const currentSize = column.getSize();
    const finalSize = currentSize > 0 ? currentSize : columnDefSize || 50;

    return {
      width: `calc(var(--col-${columnId}-size, ${finalSize}) * 1px)`,
      minWidth: `${finalSize}px`,
      left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
      right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    };
  }, [column, columnId]);

  const columnCellClassNames = useMemo(() => {
    const isPinned = column.getIsPinned();
    const columnMeta = column.columnDef.meta as
      | Record<string, unknown>
      | undefined;
    const cellClassName = columnMeta?.cellClassName as string | undefined;

    return classNames(
      'tanstack-table--td',
      {
        'td--pinned': isPinned,
        'td--pinned__left':
          isPinned === 'left' && column.getIsLastColumn('left'),
        'td--pinned__right':
          isPinned === 'right' && column.getIsLastColumn('right'),
      },
      cellClassName,
    );
  }, [column]);

  // Memoize checkbox handler to prevent unnecessary re-renders
  const handleCheckboxChange = useCallback(
    (checked: boolean) => {
      setOptimisticChecked(checked); // instant visual feedback
      onRowSelectHandler(rowId, checked);
    },
    [onRowSelectHandler, rowId],
  );

  // Memoize skeleton to prevent recreation
  const skeleton = useMemo(() => {
    const columnMeta = column.columnDef.meta as any;
    const skeletonValue =
      columnMeta?.skeleton || (column.columnDef as any).skeleton;

    if (skeletonValue) {
      return skeletonValue;
    }

    // Default skeleton if not provided
    return <SkeletonLoading width={100} height={18} />;
  }, [column]);

  // Memoize checkbox rendering
  const selectCheckbox = useMemo(() => {
    if (isDisabled && !isRowSelected) {
      const tooltipText =
        getRowDisabledTooltip && rowData ? getRowDisabledTooltip(rowData) : '';

      // Only render tooltip if there's text to display
      if (tooltipText) {
        return (
          <OverlayTooltip
            text={tooltipText}
            placement={Placement.BottomStart}
            className="max-rows-selected"
          >
            <div className="column-cell-content--checkbox">
              <Checkbox
                checked={isRowSelected}
                className="show-disable-style"
                disabled={isDisabled}
              />
            </div>
          </OverlayTooltip>
        );
      }

      // No tooltip, just disabled checkbox
      return (
        <div className="column-cell-content--checkbox">
          <Checkbox
            checked={isRowSelected}
            className="show-disable-style"
            disabled={isDisabled}
          />
        </div>
      );
    }
    return (
      <div className="column-cell-content--checkbox">
        <Checkbox
          checked={optimisticChecked}
          onChange={handleCheckboxChange}
          disabled={isDisabled}
        />
      </div>
    );
  }, [
    isDisabled,
    isRowSelected,
    optimisticChecked,
    getRowDisabledTooltip,
    rowData,
    handleCheckboxChange,
  ]);

  // Memoize row click handler
  const handleRowClick = useMemo(
    () =>
      isFirstColumn && onRowClick && !isLoading
        ? accessibleOnClick(() => onRowClick(row.original))
        : {},
    [isFirstColumn, onRowClick, isLoading, row.original],
  );

  return (
    <div className={columnCellClassNames} style={columnCellStyles}>
      <div className="column-cell-content">
        {enableRowSelection && isFirstColumn && !isLoading && selectCheckbox}
        <div className="column-cell-content--value" {...handleRowClick}>
          {isLoading
            ? skeleton
            : flexRender(column.columnDef.cell, cell.getContext())}
        </div>
        <div className="column-cell-content--resizer" />
      </div>
    </div>
  );
};

export default React.memo(ColumnCell, (prevProps, nextProps) => {
  const columnDefChanged =
    prevProps.cell.column.columnDef !== nextProps.cell.column.columnDef;

  // Re-render if the row's original object reference changed
  const rowOriginalChanged = prevProps.row.original !== nextProps.row.original;

  // Optimize: selectAll : isRowSelected / isDisabled only drive the selection checkbox, 
  const isSelectionCell =
    nextProps.cellIndex === 0 && nextProps.enableRowSelection;
  const selectionStateEqual = isSelectionCell
    ? prevProps.isRowSelected === nextProps.isRowSelected &&
      prevProps.isDisabled === nextProps.isDisabled
    : true;

  const shouldSkipRender =
    !columnDefChanged &&
    !rowOriginalChanged &&
    selectionStateEqual &&
    prevProps.isRowExpanded === nextProps.isRowExpanded &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.enableRowSelection === nextProps.enableRowSelection &&
    prevProps.cell.id === nextProps.cell.id &&
    prevProps.row.id === nextProps.row.id &&
    prevProps.onRowSelectHandler === nextProps.onRowSelectHandler &&
    prevProps.onRowClick === nextProps.onRowClick &&
    prevProps.cell.column.columnDef.cell ===
      nextProps.cell.column.columnDef.cell &&
    prevProps.cell.column.getIsPinned() === nextProps.cell.column.getIsPinned();
  return shouldSkipRender;
});
